import { normalizeEvent } from "@tsmono/inspect-common/normalize";
import {
  AttachmentData,
  ChatMessage,
  JsonValue,
  ModelEvent,
} from "@tsmono/inspect-common/types";
import { createLogger } from "@tsmono/util";

import {
  ClientAPI,
  EventData,
  SampleData,
  SampleDataResponse,
} from "../client/api/types";
import { resolveAttachments } from "../utils/attachments";

const log = createLogger("sampleStream");

const kNoId = -1;

/** A resolved sample event (the discriminated union `EventData` streams). */
export type SampleEvent = EventData["event"];

export interface SampleStreamTick {
  /**
   * All events resolved so far this session. The array identity is stable
   * across ticks that change nothing (transcript rendering memoizes on it).
   */
  events: SampleEvent[];
  /**
   * Streaming should finalize: the buffer is flushed/complete (or the log
   * summary reports the sample completed) with no further deltas — fetch the
   * completed EvalSample instead of ticking again.
   */
  done: boolean;
  /**
   * Set when `done` because the buffer itself reported complete. The flushed
   * EvalSample may not be readable yet, so a missing EvalSample is a transient
   * condition rather than an error in this case.
   */
  bufferComplete: boolean;
  /**
   * The transport's `has_more` from an OK data response — more backlog is
   * waiting server-side. Undefined when this tick carried no data signal
   * (NotFound / finalize / non-OK response), so the caller can distinguish
   * "caught up" from "no news".
   */
  hasMore?: boolean;
  /** This tick moved at least one cursor (new events/attachments/pools). */
  advanced: boolean;
}

/**
 * A per-sample streaming session over `api.get_log_sample_data`: owns the
 * cursors, message/call pools, attachment map, and event list for one
 * (logFile, id, epoch). Mechanism only — UI-ignorant and framework-free; the
 * caller decides when to tick and supplies the one outside fact the finalize
 * decision needs (`completedInLog`).
 */
export interface SampleStreamSession {
  tick(completedInLog: boolean): Promise<SampleStreamTick>;
  reset(): void;
}

interface StreamState {
  eventId: number;
  attachmentId: number;
  messagePoolId: number;
  callPoolId: number;

  attachments: Record<string, string>;
  messagePool: ChatMessage[];
  callPool: JsonValue[];
  messagePoolEntryIds: Set<number>;
  callPoolEntryIds: Set<number>;

  // event_id -> index in `events`, so a re-streamed event replaces in place.
  eventMapping: Record<string, number>;
  events: SampleEvent[];

  // Missing attachment ids already reported: an event can reference the same
  // id many times (and events legitimately arrive a tick ahead of their
  // attachments), so report each id once per session rather than per miss.
  reportedAttachmentMisses: Set<string>;
}

const initialStreamState = (): StreamState => ({
  eventId: kNoId,
  attachmentId: kNoId,
  messagePoolId: kNoId,
  callPoolId: kNoId,

  attachments: {},
  messagePool: [],
  callPool: [],
  messagePoolEntryIds: new Set(),
  callPoolEntryIds: new Set(),

  eventMapping: {},
  events: [],

  reportedAttachmentMisses: new Set(),
});

export const createSampleStreamSession = (
  api: ClientAPI,
  logFile: string,
  id: string | number,
  epoch: number
): SampleStreamSession => {
  let state = initialStreamState();
  // The last array handed out; replaced only when events actually change so
  // no-op ticks don't churn identity.
  let emittedEvents: SampleEvent[] = [];

  const tick = async (completedInLog: boolean): Promise<SampleStreamTick> => {
    if (!api.get_log_sample_data) {
      throw new Error("Required API get_log_sample_data is undefined.");
    }

    // One response per tick: each segment lands (and paints) as it arrives.
    // has_more catch-up cadence is the caller's decision, driven by the
    // returned `hasMore`/`advanced` signals.
    const priorEventId = state.eventId;
    const priorAttachmentId = state.attachmentId;
    const priorMessagePoolId = state.messagePoolId;
    const priorCallPoolId = state.callPoolId;

    const response = await api.get_log_sample_data(
      logFile,
      id,
      epoch,
      priorEventId,
      priorAttachmentId,
      priorMessagePoolId,
      priorCallPoolId
    );

    if (response?.status === "NotFound") {
      return {
        events: emittedEvents,
        done: true,
        bufferComplete: false,
        advanced: false,
      };
    }

    if (shouldFinalizeStreamingSample(response, completedInLog)) {
      return {
        events: emittedEvents,
        done: true,
        bufferComplete: response?.complete === true,
        advanced: false,
      };
    }

    if (response?.status === "OK" && response.sampleData) {
      processAttachments(response.sampleData, state);
      // Pool entries must land before events so refs can be resolved.
      processMessagePool(response.sampleData, state);
      processCallPool(response.sampleData, state);
      const eventsChanged = processEvents(
        response.sampleData,
        state,
        api,
        logFile
      );

      state.attachmentId = findMaxId(
        response.sampleData.attachments,
        state.attachmentId
      );
      state.eventId = findMaxId(response.sampleData.events, state.eventId);

      if (eventsChanged) {
        emittedEvents = [...state.events];
      }

      const advanced =
        state.eventId > priorEventId ||
        state.attachmentId > priorAttachmentId ||
        state.messagePoolId > priorMessagePoolId ||
        state.callPoolId > priorCallPoolId;
      return {
        events: emittedEvents,
        done: false,
        bufferComplete: false,
        hasMore: response.has_more === true,
        advanced,
      };
    }

    return {
      events: emittedEvents,
      done: false,
      bufferComplete: false,
      advanced: false,
    };
  };

  const reset = () => {
    state = initialStreamState();
    emittedEvents = [];
  };

  return { tick, reset };
};

export const hasSampleDataUpdates = (sampleData?: SampleData) => {
  if (!sampleData) {
    return false;
  }

  return (
    sampleData.events.length > 0 ||
    sampleData.attachments.length > 0 ||
    sampleData.message_pool.length > 0 ||
    sampleData.call_pool.length > 0
  );
};

export const shouldFinalizeStreamingSample = (
  sampleDataResponse: SampleDataResponse | undefined,
  completedInLog: boolean | undefined
): boolean => {
  if (!sampleDataResponse) {
    return false;
  }

  if (sampleDataResponse.status === "NotModified") {
    return completedInLog === true;
  }

  if (sampleDataResponse.status !== "OK") {
    return false;
  }

  if (sampleDataResponse.has_more === true) {
    return false;
  }

  if (!completedInLog && sampleDataResponse.complete !== true) {
    return false;
  }

  return !hasSampleDataUpdates(sampleDataResponse.sampleData);
};

function processAttachments(sampleData: SampleData, state: StreamState) {
  log.debug(`Processing ${sampleData.attachments.length} attachments`);
  Object.values(sampleData.attachments).forEach((v) => {
    state.attachments[v.hash] = v.content;
  });
}

function processMessagePool(sampleData: SampleData, state: StreamState) {
  for (const entry of sampleData.message_pool) {
    state.messagePoolId = Math.max(state.messagePoolId, entry.id);
    if (state.messagePoolEntryIds.has(entry.id)) continue;

    state.messagePoolEntryIds.add(entry.id);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- wire boundary (#555): pool entries are JSON strings the server wrote; the transcript renders defensively
    state.messagePool.push(JSON.parse(entry.data) as ChatMessage);
  }
}

function processCallPool(sampleData: SampleData, state: StreamState) {
  for (const entry of sampleData.call_pool) {
    state.callPoolId = Math.max(state.callPoolId, entry.id);
    if (state.callPoolEntryIds.has(entry.id)) continue;

    state.callPoolEntryIds.add(entry.id);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- wire boundary (#555): see processMessagePool
    state.callPool.push(JSON.parse(entry.data) as JsonValue);
  }
}

function processEvents(
  sampleData: SampleData,
  state: StreamState,
  api: ClientAPI,
  logFile: string
): boolean {
  log.debug(`Processing ${sampleData.events.length} events`);
  if (sampleData.events.length === 0) {
    return false;
  }

  for (const eventData of sampleData.events) {
    const existingIndex = state.eventMapping[eventData.event_id];

    // Boundary normalization (#555): pending-sample-data events arrive raw —
    // the direct transport reads filestore segments without pydantic, and
    // the proxy can front an older server. Fill read-time defaults before
    // ref resolution; un-event-shaped payloads pass through unchanged.
    const event =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- boundary lift (#555): the same lift the transports make — Event and the hand-written SampleEvent union differ only in member breadth
      (normalizeEvent(eventData.event) as SampleEvent | undefined) ??
      eventData.event;

    const reportAttachmentMiss = (attachmentId: string) => {
      if (state.reportedAttachmentMisses.has(attachmentId)) {
        return;
      }
      state.reportedAttachmentMisses.add(attachmentId);

      const snapshot = {
        eventId: eventData.event_id,
        attachmentId,
        available_attachment_count: Object.keys(state.attachments).length,
      };

      if (api.log_message) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        api.log_message(
          logFile,
          `Unable to resolve attachment ${attachmentId}\n` +
            JSON.stringify(snapshot)
        );
      }
      console.warn(`Unable to resolve attachment ${attachmentId}`, snapshot);
    };

    const withAttachments = resolveAttachments<SampleEvent>(
      event,
      state.attachments,
      reportAttachmentMiss
    );

    const withPoolRefs = resolvePoolRefs(withAttachments, state);

    // Resolve attachments again after pool expansion, since pool entries
    // may contain attachment:// URIs that weren't visible before expansion.
    const resolvedEvent = resolveAttachments<SampleEvent>(
      withPoolRefs,
      state.attachments,
      reportAttachmentMiss
    );

    if (existingIndex !== undefined) {
      state.events[existingIndex] = resolvedEvent;
    } else {
      state.eventMapping[eventData.event_id] = state.events.length;
      state.events.push(resolvedEvent);
    }
  }
  return true;
}

function expandRefs<T>(refs: [number, number][], pool: T[]): T[] {
  return refs.flatMap(([start, end]) => pool.slice(start, end));
}

function resolvePoolRefs(event: SampleEvent, state: StreamState): SampleEvent {
  if (event.event !== "model") return event;

  const withInput =
    Array.isArray(event.input_refs) && state.messagePool.length > 0
      ? {
          ...event,
          input: expandRefs(
            event.input_refs,
            state.messagePool
          ) satisfies ModelEvent["input"],
          input_refs: null,
        }
      : event;

  if (!withInput.call || !Array.isArray(withInput.call.call_refs)) {
    return withInput;
  }

  const msgKey = withInput.call.call_key || "messages";
  const request = { ...withInput.call.request };
  request[msgKey] = expandRefs(withInput.call.call_refs, state.callPool);
  return {
    ...withInput,
    call: {
      ...withInput.call,
      request,
      call_refs: null,
      call_key: null,
    },
  };
}

const findMaxId = (
  items: EventData[] | AttachmentData[],
  currentMax: number
) =>
  items.length > 0
    ? Math.max(...items.map((i) => i.id), currentMax)
    : currentMax;
